import type { BoxTaskSettings } from "../../components/NoteWorkflows/boxTaskTypes";
import type { NoteWorkflow, NoteWorkflowsDocument, WorkflowNodeSelection } from "../../components/NoteWorkflows/noteWorkflowsTypes";
import { deleteNode, deleteSubflow, deleteSubflowStep, updateNode, updateSubflowNode } from "../../components/NoteWorkflows/noteWorkflowsMutations";
import type { BuilderWorkflow } from "../workflowBuilder/models";
import type { PendingSignPermission } from "./models";

/** Clone native authoring data and make harmless optional defaults explicit. */
export function normalizeNativeWorkflow(workflow: NoteWorkflow): NoteWorkflow {
    const normalizeSettings = (settings: BoxTaskSettings | undefined): BoxTaskSettings | undefined => {
        if (!settings) return undefined;
        return settings.permissionVersion === 2
            ? { permissionVersion: 2, signerMemberIds: [...settings.signerMemberIds], viewerMemberIds: [...settings.viewerMemberIds] }
            : { responsibleMemberIds: [...settings.responsibleMemberIds], signerMemberIds: [...settings.signerMemberIds], notificationRecipients: settings.notificationRecipients.map(recipient => ({ memberId: recipient.memberId, email: recipient.email })) };
    };
    const normalizeNode = (node: NoteWorkflow["steps"][number]) => ({
        id: node.id, title: node.title ?? "", description: node.description,
        display_mode: node.display_mode ?? "content", task_settings: normalizeSettings(node.task_settings),
        output_type: node.output_type ?? "text", options: (node.options ?? []).map(option => ({ ...option })),
        context_selectors: [...(node.context_selectors ?? [])], analytics: node.analytics ? structuredClone(node.analytics) : node.analytics, client_brain_action: node.client_brain_action ? structuredClone(node.client_brain_action) : node.client_brain_action
    });
    return {
        id: workflow.id, timezone: workflow.timezone, data_query: workflow.data_query ? structuredClone(workflow.data_query) : workflow.data_query, data_scope: workflow.data_scope ? structuredClone(workflow.data_scope) : workflow.data_scope, view_all_boxes: workflow.view_all_boxes ?? false,
        trigger: { id: workflow.trigger.id, title: workflow.trigger.title ?? "", description: workflow.trigger.description },
        steps: workflow.steps.map(normalizeNode), outcomes: workflow.outcomes.map(normalizeNode),
        subflows: (workflow.subflows ?? []).map(flow => ({ ...normalizeNode(flow), steps: flow.steps.map(normalizeNode) }))
    };
}

/** Sample clients belong to preview state, never a reusable Document definition. */
export function documentationAuthoringWorkflow(workflow: NoteWorkflow): NoteWorkflow {
    return { ...normalizeNativeWorkflow(workflow), data_scope: null };
}

/** Preserve missing legacy permissions explicitly instead of creating new v2 rules. */
export function serializeNativeWorkflow(workflow: NoteWorkflow) {
    const native = normalizeNativeWorkflow(workflow);
    const serializeNode = (node: NoteWorkflow["steps"][number]) => ({ ...node, task_settings: node.task_settings ?? null });
    return {
        ...native,
        steps: native.steps.map(serializeNode),
        outcomes: native.outcomes.map(serializeNode),
        subflows: (native.subflows ?? []).map(flow => ({ ...serializeNode(flow), steps: flow.steps.map(serializeNode) }))
    };
}

export function hasWorkflowChanges(workflow: NoteWorkflow | null, baseWorkflow: NoteWorkflow | null): boolean {
    return JSON.stringify(workflow ? serializeNativeWorkflow(workflow) : null)
        !== JSON.stringify(baseWorkflow ? serializeNativeWorkflow(baseWorkflow) : null);
}

export function workflowDocument(workflow: NoteWorkflow): NoteWorkflowsDocument {
    return { schema_version: 1, workflows: [workflow] };
}

export function outputNodes(workflow: NoteWorkflow) {
    return [...workflow.steps, ...workflow.outcomes, ...(workflow.subflows ?? []), ...(workflow.subflows ?? []).flatMap(flow => flow.steps)];
}

export function selectedNativeNode(workflow: NoteWorkflow | null, selection: WorkflowNodeSelection | null) {
    if (!workflow || selection?.workflowId !== workflow.id) return undefined;
    if (selection.nodeKind === "trigger") return workflow.trigger.id === selection.nodeId ? workflow.trigger : undefined;
    return outputNodes(workflow).find(node => node.id === selection.nodeId);
}

/** Display projection only. Formal Save always uses the original native workflow. */
export function previewDisplayWorkflow(workflow: NoteWorkflow): BuilderWorkflow {
    return {
        kind: "document", reminder: null, timezone: workflow.timezone, data_query: workflow.data_query ? structuredClone(workflow.data_query) : workflow.data_query, data_scope: workflow.data_scope ? structuredClone(workflow.data_scope) : workflow.data_scope,
        title: workflow.trigger.title ?? "", description: workflow.trigger.description,
        boxes: outputNodes(workflow).map(node => ({
            id: node.id, title: node.title ?? "", instructions: node.description,
            output_type: node.output_type ?? "text", options: (node.options ?? []).map(option => ({ ...option })), context_selectors: [...(node.context_selectors ?? [])], analytics: node.analytics ? structuredClone(node.analytics) : node.analytics, client_brain_action: node.client_brain_action ? structuredClone(node.client_brain_action) : node.client_brain_action
        }))
    };
}

export function editNativeNode(workflow: NoteWorkflow, selection: WorkflowNodeSelection, fields: { title: string; description: string; task_settings?: BoxTaskSettings }): NoteWorkflow {
    const document = workflowDocument(workflow);
    const next = selection.nodeKind === "subflow" || selection.nodeKind === "subflow_step"
        ? updateSubflowNode(document, workflow.id, selection.nodeKind, selection.nodeId, fields, selection.nodeKind === "subflow_step" ? selection.subflowId : undefined)
        : updateNode(document, workflow.id, selection.nodeKind, selection.nodeId, fields);
    return next.workflows[0];
}

export class NativeWorkflowNeedsOutputError extends Error {
    constructor() { super("Keep at least one output box."); this.name = "NativeWorkflowNeedsOutputError"; }
}

export function removeNativeNode(workflow: NoteWorkflow, selection: WorkflowNodeSelection): NoteWorkflow | null {
    const document = workflowDocument(workflow);
    const onlySubflowStep = selection.nodeKind === "subflow_step" && workflow.subflows?.find(flow => flow.id === selection.subflowId)?.steps.length === 1;
    const next = selection.nodeKind === "subflow" ? deleteSubflow(document, workflow.id, selection.nodeId)
        : selection.nodeKind === "subflow_step" ? (onlySubflowStep
            ? deleteSubflow(document, workflow.id, selection.subflowId)
            : deleteSubflowStep(document, workflow.id, selection.subflowId, selection.nodeId))
        : deleteNode(document, workflow.id, selection.nodeKind, selection.nodeId);
    const updated = next.workflows[0] ?? null;
    if (updated && !outputNodes(updated).length) throw new NativeWorkflowNeedsOutputError();
    return updated;
}

export function retainPendingNodes(pending: readonly PendingSignPermission[], workflow: NoteWorkflow | null): PendingSignPermission[] {
    const ids = new Set(workflow ? outputNodes(workflow).map(node => node.id) : []);
    return pending.filter(permission => ids.has(permission.node_id));
}

export function pendingAfterPermissionEdit(pending: readonly PendingSignPermission[], nodeId: string, previous: BoxTaskSettings | undefined, edited: BoxTaskSettings | undefined): PendingSignPermission[] {
    return JSON.stringify(previous) === JSON.stringify(edited) ? [...pending] : pending.filter(item => item.node_id !== nodeId);
}
